/**
 * BIRHAUS ESLint Plugin
 * 
 * ESLint rules to enforce BIRHAUS design principles in React/TypeScript code:
 * - Miller's Law compliance (max 7 navigation items, form fields)
 * - Spanish-first labeling requirements
 * - Undo-over-confirm pattern enforcement
 * - Accessibility compliance checks
 * - Cognitive load reduction
 */

import { Rule } from 'eslint'

/* eslint-disable @typescript-eslint/no-explicit-any */
// AST node types from ESLint are complex and often require 'any' for compatibility

export interface BirhausLinterOptions {
  // Miller's Law enforcement
  maxNavigationItems?: number
  maxFormFields?: number
  maxSelectOptions?: number
  
  // Spanish-first requirements
  requireSpanishLabels?: boolean
  spanishLabelSuffix?: string
  
  // BIRHAUS pattern enforcement
  forbidConfirmationDialogs?: boolean
  requireUndoPattern?: boolean
  
  // Accessibility requirements
  requireAltText?: boolean
  requireAriaLabels?: boolean
  
  // Cognitive load limits
  maxActionsPerView?: number
  maxColorsPerComponent?: number
}

const defaultOptions: BirhausLinterOptions = {
  maxNavigationItems: 7,
  maxFormFields: 7,
  maxSelectOptions: 7,
  requireSpanishLabels: true,
  spanishLabelSuffix: 'Es',
  forbidConfirmationDialogs: true,
  requireUndoPattern: false,
  requireAltText: true,
  requireAriaLabels: true,
  maxActionsPerView: 4,
  maxColorsPerComponent: 5
}

// BIRHAUS Rule: Miller's Law - Navigation Items
export const millerLawNavigationRule: Rule.RuleModule = {
  meta: {
    type: 'problem',
    docs: {
      description: 'Enforce Miller\'s Law (7±2) for navigation items',
      category: 'BIRHAUS Principles',
      recommended: true
    },
    fixable: undefined,
    schema: [
      {
        type: 'object',
        properties: {
          maxItems: {
            type: 'integer',
            minimum: 1,
            maximum: 10,
            default: 7
          }
        },
        additionalProperties: false
      }
    ],
    messages: {
      tooManyNavItems: 'Navigation has {{count}} items (BIRHAUS Principle #1: max {{max}} recommended for cognitive load)',
      suggestGrouping: 'Consider grouping navigation items or using dropdown menus to reduce cognitive load'
    }
  },
  
  create(context: Rule.RuleContext) {
    const options = { ...defaultOptions, ...context.options[0] }
    
    return {
      JSXElement(node: any) {
        // Detect nav elements
        if (isNavigationElement(node)) {
          const navItems = countNavigationItems(node)
          
          if (navItems > options.maxNavigationItems!) {
            context.report({
              node: node as any,
              messageId: 'tooManyNavItems',
              data: {
                count: navItems.toString(),
                max: options.maxNavigationItems!.toString()
              }
            })
          }
        }
      }
    }
  }
}

// BIRHAUS Rule: Spanish-First Labels
export const spanishFirstLabelsRule: Rule.RuleModule = {
  meta: {
    type: 'problem',
    docs: {
      description: 'Require Spanish-first labeling for BIRHAUS components',
      category: 'BIRHAUS Principles',
      recommended: true
    },
    fixable: 'code',
    schema: [
      {
        type: 'object',
        properties: {
          suffix: {
            type: 'string',
            default: 'Es'
          },
          components: {
            type: 'array',
            items: { type: 'string' },
            default: ['BirhausButton', 'BirhausInput', 'BirhausSelect', 'BirhausCard']
          }
        },
        additionalProperties: false
      }
    ],
    messages: {
      missingSpanishLabel: 'BIRHAUS component missing Spanish label (BIRHAUS Principle #7: Bilingual by design)',
      addSpanishLabel: 'Add {{labelName}} prop for Spanish-first internationalization'
    }
  },
  
  create(context: Rule.RuleContext) {
    const options = { ...defaultOptions, ...context.options[0] }
    const birhausComponents = options.components || ['BirhausButton', 'BirhausInput', 'BirhausSelect', 'BirhausCard']
    
    return {
      JSXElement(node: any) {
        if (isBirhausComponent(node, birhausComponents)) {
          const missingSpanishLabels = findMissingSpanishLabels(node, options.spanishLabelSuffix!)
          
          missingSpanishLabels.forEach(labelName => {
            context.report({
              node: node as any,
              messageId: 'missingSpanishLabel',
              data: { labelName },
              fix(fixer) {
                // Auto-fix by adding Spanish label prop
                return fixer.insertTextBefore(node as any, `${labelName}="[TODO: Spanish text]" `)
              }
            })
          })
        }
      }
    }
  }
}

// BIRHAUS Rule: Forbid Confirmation Dialogs
export const forbidConfirmationDialogsRule: Rule.RuleModule = {
  meta: {
    type: 'problem',
    docs: {
      description: 'Forbid confirmation dialogs in favor of undo patterns',
      category: 'BIRHAUS Principles',
      recommended: true
    },
    fixable: undefined,
    schema: [],
    messages: {
      confirmationDialogFound: 'Confirmation dialog detected (BIRHAUS Principle #5: Undo over confirm)',
      useUndoPattern: 'Replace confirmation with undo functionality for better UX'
    }
  },
  
  create(context: Rule.RuleContext) {
    const confirmationPatterns = [
      /confirm/i,
      /¿está\s+seguro\?/i,
      /are\s+you\s+sure\?/i,
      /delete.*confirmation/i,
      /eliminar.*confirmación/i
    ]
    
    return {
      JSXElement(node: any) {
        if (containsConfirmationPattern(node, confirmationPatterns)) {
          context.report({
            node: node as any,
            messageId: 'confirmationDialogFound'
          })
        }
      },
      
      CallExpression(node: any) {
        // Detect window.confirm() calls
        if (node.callee.type === 'MemberExpression' &&
            node.callee.object.name === 'window' &&
            node.callee.property.name === 'confirm') {
          context.report({
            node,
            messageId: 'confirmationDialogFound'
          })
        }
        
        // Detect confirm() calls
        if (node.callee.type === 'Identifier' && node.callee.name === 'confirm') {
          context.report({
            node,
            messageId: 'confirmationDialogFound'
          })
        }
      }
    }
  }
}

// BIRHAUS Rule: Form Field Limits
export const formFieldLimitsRule: Rule.RuleModule = {
  meta: {
    type: 'problem',
    docs: {
      description: 'Limit form fields per form to reduce cognitive load',
      category: 'BIRHAUS Principles',
      recommended: true
    },
    fixable: undefined,
    schema: [
      {
        type: 'object',
        properties: {
          maxFields: {
            type: 'integer',
            minimum: 1,
            maximum: 15,
            default: 7
          }
        },
        additionalProperties: false
      }
    ],
    messages: {
      tooManyFormFields: 'Form has {{count}} fields (BIRHAUS Principle #4: max {{max}} recommended)',
      useProgressiveDisclosure: 'Consider using multi-step forms or progressive disclosure for better UX'
    }
  },
  
  create(context: Rule.RuleContext) {
    const options = { ...defaultOptions, ...context.options[0] }
    
    return {
      JSXElement(node: any) {
        if (isFormElement(node)) {
          const fieldCount = countFormFields(node)
          
          if (fieldCount > options.maxFormFields!) {
            context.report({
              node: node as any,
              messageId: 'tooManyFormFields',
              data: {
                count: fieldCount.toString(),
                max: options.maxFormFields!.toString()
              }
            })
          }
        }
      }
    }
  }
}

// BIRHAUS Rule: Accessibility Requirements
export const accessibilityRequirementsRule: Rule.RuleModule = {
  meta: {
    type: 'problem',
    docs: {
      description: 'Enforce accessibility requirements for BIRHAUS components',
      category: 'BIRHAUS Principles',
      recommended: true
    },
    fixable: 'code',
    schema: [],
    messages: {
      missingAltText: 'Image missing alt text (BIRHAUS Principle #6: Accessibility = dignity)',
      missingAriaLabel: 'Interactive element missing aria-label (BIRHAUS Principle #6)',
      addAccessibilityAttribute: 'Add {{attribute}} for accessibility compliance'
    }
  },
  
  create(context: Rule.RuleContext) {
    return {
      JSXElement(node: any) {
        // Check images for alt text
        if (isImageElement(node) && !hasAttribute(node, 'alt')) {
          context.report({
            node: node as any,
            messageId: 'missingAltText',
            fix(fixer) {
              return fixer.insertTextBefore(node as any, 'alt="[TODO: Describe image]" ')
            }
          })
        }
        
        // Check interactive elements for aria-label
        if (isInteractiveElement(node) && !hasAccessibleLabel(node)) {
          context.report({
            node: node as any,
            messageId: 'missingAriaLabel',
            data: { attribute: 'aria-label' }
          })
        }
      }
    }
  }
}

// Utility functions
function isNavigationElement(node: any): boolean {
  const elementName = getElementName(node)
  return ['nav', 'Navigation', 'NavBar', 'Menu'].includes(elementName)
}

function isBirhausComponent(node: any, components: string[]): boolean {
  const elementName = getElementName(node)
  return components.includes(elementName)
}

function isFormElement(node: any): boolean {
  const elementName = getElementName(node)
  return ['form', 'Form', 'BirhausForm'].includes(elementName)
}

function isImageElement(node: any): boolean {
  const elementName = getElementName(node)
  return elementName === 'img'
}

function isInteractiveElement(node: any): boolean {
  const elementName = getElementName(node)
  return ['button', 'a', 'input', 'select', 'textarea'].includes(elementName.toLowerCase())
}

function getElementName(node: any): string {
  if (node.openingElement.name.type === 'JSXIdentifier') {
    return node.openingElement.name.name
  }
  return ''
}

function countNavigationItems(node: any): number {
  // Simple implementation - count direct child links/buttons
  let count = 0
  
  function traverse(n: any) {
    if (n.type === 'JSXElement') {
      const name = getElementName(n)
      if (['a', 'button', 'Link', 'NavLink'].includes(name)) {
        count++
      }
      
      if (n.children) {
        n.children.forEach(traverse)
      }
    }
  }
  
  if (node.children) {
    node.children.forEach(traverse)
  }
  
  return count
}

function countFormFields(node: any): number {
  let count = 0
  
  function traverse(n: any) {
    if (n.type === 'JSXElement') {
      const name = getElementName(n)
      if (['input', 'select', 'textarea', 'BirhausInput', 'BirhausSelect'].includes(name)) {
        count++
      }
      
      if (n.children) {
        n.children.forEach(traverse)
      }
    }
  }
  
  if (node.children) {
    node.children.forEach(traverse)
  }
  
  return count
}

function findMissingSpanishLabels(node: any, suffix: string): string[] {
  const missing: string[] = []
  const attributes = node.openingElement.attributes || []
  
  // Common label props that should have Spanish equivalents
  const labelProps = ['label', 'title', 'placeholder', 'buttonText', 'description']
  
  labelProps.forEach(prop => {
    const hasBaseProp = attributes.some((attr: any) => 
      attr.type === 'JSXAttribute' && 
      attr.name?.type === 'JSXIdentifier' && 
      attr.name?.name === prop
    )
    
    if (hasBaseProp) {
      const spanishProp = prop + suffix
      const hasSpanishProp = attributes.some((attr: any) =>
        attr.type === 'JSXAttribute' && 
        attr.name?.type === 'JSXIdentifier' && 
        attr.name?.name === spanishProp
      )
      
      if (!hasSpanishProp) {
        missing.push(spanishProp)
      }
    }
  })
  
  return missing
}

function containsConfirmationPattern(node: any, patterns: RegExp[]): boolean {
  // Check text content and attributes for confirmation patterns
  const text = extractTextContent(node)
  return patterns.some(pattern => pattern.test(text))
}

function hasAttribute(node: any, attributeName: string): boolean {
  const attributes = node.openingElement.attributes || []
  return attributes.some((attr: any) =>
    attr.type === 'JSXAttribute' && 
    attr.name?.type === 'JSXIdentifier' && 
    attr.name?.name === attributeName
  )
}

function hasAccessibleLabel(node: any): boolean {
  return hasAttribute(node, 'aria-label') || 
         hasAttribute(node, 'aria-labelledby') || 
         hasAttribute(node, 'title')
}

function extractTextContent(node: any): string {
  // Simple text extraction - in a real implementation, this would be more thorough
  let text = ''
  
  function traverse(n: any) {
    if (n.type === 'JSXText') {
      text += n.value
    } else if (n.type === 'JSXElement' && n.children) {
      n.children.forEach(traverse)
    }
  }
  
  if (node.children) {
    node.children.forEach(traverse)
  }
  
  return text
}

// Export all rules
export const birhausRules = {
  'miller-law-navigation': millerLawNavigationRule,
  'spanish-first-labels': spanishFirstLabelsRule,
  'forbid-confirmation-dialogs': forbidConfirmationDialogsRule,
  'form-field-limits': formFieldLimitsRule,
  'accessibility-requirements': accessibilityRequirementsRule
}

// ESLint plugin configuration
export const birhausPlugin = {
  rules: birhausRules,
  configs: {
    recommended: {
      plugins: ['@birhaus'],
      rules: {
        '@birhaus/miller-law-navigation': 'error',
        '@birhaus/spanish-first-labels': 'warn',
        '@birhaus/forbid-confirmation-dialogs': 'error',
        '@birhaus/form-field-limits': 'warn',
        '@birhaus/accessibility-requirements': 'error'
      }
    },
    strict: {
      plugins: ['@birhaus'],
      rules: {
        '@birhaus/miller-law-navigation': 'error',
        '@birhaus/spanish-first-labels': 'error',
        '@birhaus/forbid-confirmation-dialogs': 'error',
        '@birhaus/form-field-limits': 'error',
        '@birhaus/accessibility-requirements': 'error'
      }
    }
  }
}

export default birhausPlugin